// ANETT HORVATH — ContentRenderer Component
// Renders a content block respecting render policy.

import { useStore } from '../store/database';
import { getBlock, getBlockValue, type ContentBlock } from '../store/content-blocks';
import { canRender, buildViewerContext, type MembershipAccess } from '../core/render-policy';
import { useTranslation } from 'react-i18next';
import type { SupportedLang } from '../config/house.config';
import MediaImage from './ui/MediaImage';

interface ContentRendererProps {
  blockKey: string;
  as?: 'p' | 'h1' | 'h2' | 'h3' | 'span' | 'div';
  className?: string;
  previewAs?: MembershipAccess | null;
  fallback?: string;
  imageClass?: string;
}

export default function ContentRenderer({ blockKey, as: Tag = 'span', className = '', previewAs, fallback, imageClass }: ContentRendererProps) {
  const { i18n } = useTranslation();
  const session = useStore(s => s.session);
  const getMember = useStore(s => s.getMember);
  const member = getMember();
  const lang = (i18n.language || 'en') as SupportedLang;

  const block = getBlock(blockKey);
  if (!block) return fallback ? <Tag className={className}>{fallback}</Tag> : null;

  const ctx = buildViewerContext(session, member, previewAs);
  if (!canRender(block, ctx)) return null;

  if (block.type === 'image') {
    const url = getBlockValue(blockKey, lang);
    return <MediaImage src={url} alt={blockKey} className={imageClass || className} />;
  }

  const value = getBlockValue(blockKey, lang);
  if (!value) return null;

  return <Tag className={className}>{value}</Tag>;
}
